import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { confirmPrintfulOrder } from '../apiClient';

const PaymentForm = ({ orderId }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [isPaymentElementReady, setIsPaymentElementReady] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isTermsAccepted) {
      setMessage("Please accept the terms and policies to proceed.");
      return;
    }

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      return;
    }

    setIsLoading(true);
    setMessage(null);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    });

    if (error) {
      setMessage(error.message);
      setIsLoading(false);
      return;
    }

    if (paymentIntent) {
      if (paymentIntent.status === 'succeeded') {
        setMessage('Payment succeeded! Confirming order...');
        navigate('/payment-success', { state: { paymentIntentId: paymentIntent.id, orderId: orderId } });
        // Confirm order in the background
        confirmPrintfulOrder(orderId).catch(confirmError => {
          console.error('Error confirming Printful order:', confirmError);
          // Optionally, notify the user that the order confirmation failed
        });
      } else {
        setMessage(`Payment status: ${paymentIntent.status}`);
      }
    }

    setIsLoading(false);
  };

  const paymentElementOptions = {
    layout: "tabs"
  };

  return (
    <form id="payment-form" onSubmit={handleSubmit}>
      <PaymentElement id="payment-element" options={paymentElementOptions} onReady={() => setIsPaymentElementReady(true)} />

      <div className="flex items-start mt-4 mb-4">
        <input
          id="terms"
          type="checkbox"
          checked={isTermsAccepted}
          onChange={(e) => setIsTermsAccepted(e.target.checked)}
          className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
        />
        <label htmlFor="terms" className="ml-2 block text-sm text-gray-700 leading-relaxed">
          I agree to the{' '}
          <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Terms of Service</a>,{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Privacy Policy</a>,{' '}
          <a href="/refund-policy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Refund Policy</a>,{' '}
          and the{' '}
          <a href="/ai-tool-terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Third-Party AI Tool Terms</a>.
          I confirm I have the right to use the artwork on this order (including AI-generated or uploaded designs) and that its use does not infringe third-party intellectual property rights.
        </label>
      </div>
      <button disabled={isLoading || !stripe || !elements || !isPaymentElementReady || !isTermsAccepted} id="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md transition-colors mt-4 disabled:opacity-50 disabled:cursor-not-allowed">
        <span id="button-text">
          {isLoading ? <div className="spinner" id="spinner"></div> : "Pay now"}
        </span>
      </button>
      {/* Show any error or success messages */}
      {message && <div id="payment-message" className={message.includes('succeeded') ? 'text-green-500' : 'text-red-500'}>{message}</div>}
    </form>
  );
}

export default PaymentForm;
